import api from '@/shared/api';
import {
  withQuery,
  buildTruthySearchParams,
  requestExcelBlobExport,
  type ApiResponse,
} from '@/shared/lib';
import type {
  BulkDeleteNoticesResult,
  NoticeGeneralHistoryItem,
  NoticeHistoryItem,
  NoticeListQuery,
  NoticeListResponse,
  NoticeNotificationCenterSummary,
  NoticeRecentItem,
  NoticeRecipientsResponse,
  NoticeUnreadCount,
} from '../model/types';

type NoticeHistoryExcelExportResult = {
  blob: Blob;
  exportedCount: number;
  filename: string;
};

const EXPORT_QUERY_KEYS = [
  'term',
  'sort',
  'order',
  'createdAt',
  'createdBy',
  'recipientId',
  'category',
] as const satisfies readonly (keyof NoticeListQuery)[];

class ReminderHistoryApi {
  private buildQuery(params?: NoticeListQuery): string {
    if (!params) {
      return '';
    }

    const queryMap = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
      term: params.term,
      sort: params.sort,
      order: params.order,
      createdAt: params.createdAt,
      createdBy: params.createdBy,
      recipientId: params.recipientId,
      category: params.category,
      includeRecipients: params.includeRecipients,
    };
    return withQuery('', queryMap);
  }

  async getMyHistory(params?: NoticeListQuery): Promise<NoticeListResponse<NoticeHistoryItem>> {
    const response = await api.get<NoticeListResponse<NoticeHistoryItem>>(
      `/notices/my-history${this.buildQuery(params)}`
    );

    return response.data;
  }

  async getGeneralHistory(
    params?: NoticeListQuery
  ): Promise<NoticeListResponse<NoticeGeneralHistoryItem>> {
    const response = await api.get<NoticeListResponse<NoticeGeneralHistoryItem>>(
      `/notices/general-history${this.buildQuery(params)}`
    );

    return response.data;
  }

  async getUnreadCount(): Promise<NoticeUnreadCount> {
    const response = await api.get<ApiResponse<NoticeUnreadCount>>('/notices/unread-count');
    return response.data.data ?? { count: 0 };
  }

  async getNotificationCenterSummary(limit = 10): Promise<NoticeNotificationCenterSummary> {
    const response = await api.get<ApiResponse<NoticeNotificationCenterSummary>>(
      withQuery('/notices/notification-center', { limit })
    );

    return response.data.data ?? { unreadCount: 0, notices: [] };
  }

  async getRecent(limit = 10): Promise<NoticeRecentItem[]> {
    const response = await api.get<ApiResponse<NoticeRecentItem[]>>(
      withQuery('/notices/recent', { limit })
    );
    return response.data.data ?? [];
  }

  async acknowledgeNotice(id: number): Promise<void> {
    await api.patch(`/notices/${id}/acknowledge`);
  }

  async deleteFromGeneralHistory(id: number): Promise<void> {
    await api.delete(`/notices/${id}`);
  }

  async bulkDeleteFromGeneralHistory(ids: number[]): Promise<{ data: BulkDeleteNoticesResult }> {
    const response = await api.delete('/notices/bulk', { data: { ids } });
    return { data: response.data.data };
  }

  async getNoticeRecipients(id: number): Promise<NoticeRecipientsResponse> {
    const response = await api.get<ApiResponse<NoticeRecipientsResponse>>(
      `/notices/${id}/recipients`
    );

    return (
      response.data.data ?? {
        noticeId: id,
        recipientCount: 0,
        acknowledgedCount: 0,
        recipients: [],
      }
    );
  }

  async exportMyHistoryExcel(params?: NoticeListQuery): Promise<NoticeHistoryExcelExportResult> {
    const queryParams = buildTruthySearchParams(params ?? {}, EXPORT_QUERY_KEYS);

    return requestExcelBlobExport(
      () =>
        api.get(`/notices/my-history/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'meu-historico-avisos.xlsx',
      'Tente novamente.'
    );
  }

  async exportGeneralHistoryExcel(
    params?: NoticeListQuery
  ): Promise<NoticeHistoryExcelExportResult> {
    const queryParams = buildTruthySearchParams(params ?? {}, EXPORT_QUERY_KEYS);

    return requestExcelBlobExport(
      () =>
        api.get(`/notices/general-history/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'historico-geral-avisos.xlsx',
      'Tente novamente.'
    );
  }
}

export const reminderHistoryApi = new ReminderHistoryApi();
